import React, { useMemo } from 'react';
import {
  Popper,
  Paper,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Box,
  IconButton,
  ClickAwayListener,
  CircularProgress,
} from '@mui/material';
import { Search as SearchIcon, History as HistoryIcon, Close as CloseIcon } from '@mui/icons-material';

export interface SearchSuggestionsProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  query: string;
  recentSearches: string[];
  categoryOptions: string[];
  productOptions: string[];
  brandOptions: string[];
  placeOptions?: string[];
  placeLoading?: boolean;
  onSelect: (term: string) => void;
  onClose: () => void;
  onClearRecent: () => void;
  onRemoveRecent: (term: string) => void;
}

const SearchSuggestions: React.FC<SearchSuggestionsProps> = ({
  anchorEl,
  open,
  query,
  recentSearches,
  categoryOptions,
  productOptions,
  brandOptions,
  placeOptions,
  placeLoading,
  onSelect,
  onClose,
  onClearRecent,
  onRemoveRecent,
}) => {
  const normalized = query.trim().toLowerCase();

  const categoryMatches = useMemo(() => {
    if (!normalized) return categoryOptions.slice(0, 10);
    return categoryOptions
      .filter((t) => t.toLowerCase().includes(normalized))
      .slice(0, 12);
  }, [normalized, categoryOptions]);

  const recentFiltered = useMemo(() => {
    if (!normalized) return recentSearches.slice(0, 6);
    return recentSearches.filter((q) => q.toLowerCase().includes(normalized)).slice(0, 6);
  }, [recentSearches, normalized]);

  const productMatches = useMemo(() => {
    if (!normalized) return productOptions.slice(0, 10);
    return productOptions.filter((p) => p.toLowerCase().includes(normalized)).slice(0, 12);
  }, [normalized, productOptions]);

  const brandMatches = useMemo(() => {
    if (!normalized) return brandOptions.slice(0, 12);
    return brandOptions.filter((b) => b.toLowerCase().includes(normalized)).slice(0, 12);
  }, [normalized, brandOptions]);

  const showEmptyState = open && !normalized && recentSearches.length === 0;

  const placeMatches = useMemo(() => {
    const opts = Array.isArray(placeOptions) ? placeOptions : [];
    if (!normalized) return opts.slice(0, 8);
    return opts.filter((t) => t.toLowerCase().includes(normalized)).slice(0, 10);
  }, [normalized, placeOptions]);

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="bottom-start"
      style={{ zIndex: 1300, width: anchorEl ? (anchorEl as HTMLElement).clientWidth : undefined }}
    >
      <ClickAwayListener onClickAway={onClose}>
        <Paper
          elevation={4}
          sx={{
            mt: 1,
            borderRadius: 2,
            overflow: 'auto',
            maxHeight: '60vh',
          }}
        >
          <Box sx={{ px: 2, py: 1.25 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Vorschläge
            </Typography>
          </Box>
          <Divider />

          {showEmptyState ? (
            <Box sx={{ px: 2, py: 2, color: 'text.secondary' }}>
              Keine bisherigen Suchen. Tippe, um zu beginnen.
            </Box>
          ) : (
            <>
              {/* Place suggestions (Nach Ort) */}
              {(placeLoading || (placeMatches && placeMatches.length > 0)) && (
                <>
                  <Box sx={{ px: 2, pt: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Orte</Typography>
                  </Box>
                  <List dense sx={{ py: 0 }}>
                    {placeLoading && (
                      <ListItemButton disabled>
                        <ListItemIcon>
                          <CircularProgress
                            size={18}
                            thickness={5}
                            sx={{
                              color: 'primary.main',
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText primary="Suche Orte…" />
                      </ListItemButton>
                    )}
                    {!placeLoading && placeMatches.map((term) => (
                      <ListItemButton key={`place-${term}`} onClick={() => onSelect(term)}>
                        <ListItemIcon>
                          <SearchIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={term} />
                      </ListItemButton>
                    ))}
                  </List>
                  <Divider />
                </>
              )}

              {/* Recent searches */}
              {recentFiltered.length > 0 && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Letzte Suchen</Typography>
                    <IconButton size="small" onClick={onClearRecent} aria-label="Letzte Suchen löschen">
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <List dense sx={{ py: 0 }}>
                    {recentFiltered.map((term) => (
                      <ListItemButton key={`recent-${term}`} onClick={() => onSelect(term)}>
                        <ListItemIcon>
                          <HistoryIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={term} />
                        <IconButton
                          edge="end"
                          size="small"
                          aria-label="entfernen"
                          onClick={(e) => { e.stopPropagation(); onRemoveRecent(term); }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </ListItemButton>
                    ))}
                  </List>
                  <Divider />
                </>
              )}

              {/* Brand suggestions */}
              {brandMatches.length > 0 && (
                <>
                  <Box sx={{ px: 2, pt: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Marken</Typography>
                  </Box>
                  <List dense sx={{ py: 0 }}>
                    {brandMatches.map((name, idx) => (
                      <ListItemButton key={`b-${idx}`} onClick={() => onSelect(name)}>
                        <ListItemIcon>
                          <SearchIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={name} />
                      </ListItemButton>
                    ))}
                  </List>
                  <Divider />
                </>
              )}

              {/* Product suggestions */}
              {productMatches.length > 0 && (
                <>
                  <Box sx={{ px: 2, pt: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Produkte</Typography>
                  </Box>
                  <List dense sx={{ py: 0 }}>
                    {productMatches.map((name, idx) => (
                      <ListItemButton key={`p-${idx}`} onClick={() => onSelect(name)}>
                        <ListItemIcon>
                          <SearchIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={name} />
                      </ListItemButton>
                    ))}
                  </List>
                  <Divider />
                </>
              )}

              {/* Category suggestions */}
              {categoryMatches.length > 0 && (
                <>
                  <Box sx={{ px: 2, pt: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Kategorien</Typography>
                  </Box>
                  <List dense sx={{ py: 0 }}>
                    {categoryMatches.map((name, idx) => (
                      <ListItemButton key={`c-${idx}`} onClick={() => onSelect(name)}>
                        <ListItemIcon>
                          <SearchIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={name} />
                      </ListItemButton>
                    ))}
                  </List>
                </>
              )}

              {/* Fallback when no results */}
              {recentFiltered.length === 0 && categoryMatches.length === 0 && productMatches.length === 0 && brandMatches.length === 0 && (
                <Box sx={{ px: 2, py: 2, color: 'text.secondary' }}>
                  Keine Vorschläge gefunden.
                </Box>
              )}
            </>
          )}
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
};

export default SearchSuggestions;
